import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { useActiveOrg } from '../activeOrg';
import {
  type Capability,
  type PermissionGroup,
  sortGroups
} from '../models/permissionGroups';

export type SettingsPermissionGroupsStatus =
  | 'loading'
  | 'ready'
  | 'unauth'
  | 'empty'
  | 'forbidden'
  | 'error';

export type ViewerRoleSystemKey = 'owner' | 'admin' | 'member' | null;

/** Create-group modal lifecycle. `closed` until the user opens it. */
export type CreateGroupStatus = 'closed' | 'open' | 'saving';

/** Inline create-modal error, discriminated by the field it targets. */
export interface CreateGroupError {
  field: 'name' | 'description' | 'form';
  message: string;
}

/** Inline editor save error, discriminated by the field it targets. */
export interface SaveGroupError {
  field: 'name' | 'description' | 'form';
  message: string;
}

/** Editor save lifecycle. */
export type SaveGroupStatus = 'idle' | 'saving';

/** Delete lifecycle for the selected custom group. `idle` = no delete in
 *  progress; `blocked` = refused because members are still assigned;
 *  `confirm` = typed-name confirm panel open; `deleting` = RPC in flight. */
export type DeleteGroupStatus = 'idle' | 'blocked' | 'confirm' | 'deleting';

/** In-progress edit of the selected custom group. Seeded from the saved group
 *  on first edit; null when the editor is pristine. */
export interface GroupDraft {
  name: string;
  description: string;
  capabilityKeys: string[];
}

/** Starting point for a new custom group — `''` is Blank, otherwise clone from
 *  the named system group. Maps to the `create_permission_group` RPC's
 *  `p_clone_from` parameter (null for Blank). */
export type CloneFromOption = '' | 'admin' | 'member';

export interface CreateGroupInput {
  name: string;
  description: string;
  cloneFrom: CloneFromOption;
}

const NAME_PATTERN = /^[A-Za-z0-9 -]{2,40}$/;
const RESERVED_GROUP_NAMES = ['owner', 'admin', 'member'];

const NAME_FORMAT_ERROR =
  'Name must be 2–40 chars: letters, numbers, spaces, dashes.';
const RESERVED_NAME_ERROR = 'That name is reserved for system groups.';
const NAME_TAKEN_ERROR = 'Another group already uses that name.';
const DESCRIPTION_ERROR = 'Description must be 280 characters or fewer.';
const NETWORK_ERROR = 'Something went wrong. Try again.';
const FORBIDDEN_ERROR = 'Only Owners can edit custom permission groups.';
const UNKNOWN_CAPABILITY_ERROR =
  "One of those capabilities doesn't exist. Reload and try again.";
const NAME_MISMATCH_ERROR =
  "Typed name doesn't match. Type the group's name exactly to confirm.";
const SAVE_TOAST_MESSAGE =
  'Saved. Changes apply to all members of this group within ~30s.';

export interface UseSettingsPermissionGroupsVM {
  status: SettingsPermissionGroupsStatus;
  /** Active (home) org id. Null until the membership load resolves. */
  orgId: string | null;
  orgName: string | null;
  /** Viewer's permission_role.system_key in the active org. */
  viewerRole: ViewerRoleSystemKey;
  /** Owner — read + (6.3) write. Owner/Admin both reach the `ready` view;
   *  Members get `forbidden`. */
  canManage: boolean;
  /** System groups in fixed Owner/Admin/Member order. */
  systemGroups: PermissionGroup[];
  /** Custom groups, alphabetical by name. */
  customGroups: PermissionGroup[];
  /** Full capability vocabulary, fetched from the `capabilities` table. */
  capabilities: Capability[];
  /** Currently-selected group id (drives the right-pane editor). */
  selectedId: string | null;
  selectedGroup: PermissionGroup | null;
  /** Top-level load error message (shown via a Banner). */
  error: string | null;
  /** Create-group modal lifecycle. `closed` until the user opens it. */
  createStatus: CreateGroupStatus;
  /** Inline create-modal error, or null when the form is clean. */
  createError: CreateGroupError | null;
  /** In-progress edit of the selected custom group, or null when pristine.
   *  Seeded from the saved group on the first edit action. */
  draft: GroupDraft | null;
  /** True when `draft` diverges from the saved selected group. */
  isDirty: boolean;
  /** Editor save lifecycle. */
  saveStatus: SaveGroupStatus;
  /** Inline editor save error, or null when the form is clean. */
  saveError: SaveGroupError | null;
  /** Success-toast message after a save, or null. */
  saveToast: string | null;
  /** Delete lifecycle. `idle` unless a delete panel is open / RPC in flight. */
  deleteStatus: DeleteGroupStatus;
  /** Delete error message, or null. Shown in the delete-confirm panel. */
  deleteError: string | null;
  /** Name of the most recently deleted group — drives the success toast. */
  deleteToast: string | null;
  actions: {
    /** Select a group for the right-pane editor. Resets the edit draft. */
    select: (id: string) => void;
    /** Open the create-group modal (Owner-only — gate in the View). */
    openCreate: () => void;
    /** Close the create-group modal and clear any inline error. */
    closeCreate: () => void;
    /** Validate + create a custom group. Resolves true on success (modal
     *  closes, new group selected); false leaves the modal open with
     *  `createError` set. */
    createGroup: (input: CreateGroupInput) => Promise<boolean>;
    /** Toggle a capability key in the edit draft (seeds the draft if pristine). */
    toggleCapability: (key: string) => void;
    /** Patch the draft's name / description (seeds the draft if pristine). */
    editDraft: (patch: Partial<Pick<GroupDraft, 'name' | 'description'>>) => void;
    /** Discard the edit draft + clear any save error. */
    cancelEdit: () => void;
    /** Validate + persist the draft via the rename / capabilities RPCs.
     *  Resolves true on success (draft cleared, save toast raised). */
    saveGroup: () => Promise<boolean>;
    /** Dismiss the save success toast. */
    clearSaveToast: () => void;
    /** Open the delete panel — routes to `blocked` when the selected group
     *  still has members, else `confirm`. */
    openDelete: () => void;
    /** Close the delete panel and clear any delete error. */
    closeDelete: () => void;
    /** Validate + delete the selected custom group via the RPC. Resolves true
     *  on success (group removed, selection falls back to Owner, toast raised);
     *  false leaves the panel open with `deleteError` set or routes to
     *  `blocked` when members are still assigned. */
    deleteGroup: (confirmName: string) => Promise<boolean>;
    /** Dismiss the delete success toast. */
    clearDeleteToast: () => void;
  };
}

interface PermissionGroupRow {
  id: string;
  name: string;
  kind: 'system' | 'custom';
  system_key: string | null;
  description: string | null;
  capability_keys: string[];
}

/** jsonb envelope shape shared by the 6.3a mutation RPCs. */
type GroupRpcEnvelope =
  | { ok: true; group: PermissionGroupRow }
  | { ok: false; error: string }
  | null;

function rowToGroup(
  row: PermissionGroupRow,
  memberCount: number
): PermissionGroup {
  return {
    id: row.id,
    name: row.name,
    kind: row.kind,
    systemKey: row.system_key,
    description: row.description ?? null,
    capabilityKeys: row.capability_keys ?? [],
    memberCount
  };
}

function seedDraft(group: PermissionGroup): GroupDraft {
  return {
    name: group.name,
    description: group.description ?? '',
    capabilityKeys: [...group.capabilityKeys]
  };
}

interface CapabilityRow {
  key: string;
  resource: string;
  label: string;
  description: string | null;
  sort_order: number;
}

interface MemberRosterRow {
  kind: string;
  permission_role_name: string | null;
}

/**
 * ViewModel for `/settings/permission-groups` (turn 6.2 — read-only view).
 *
 * Loads the caller's home org (id + name) and viewer permission_role.system_key
 * using the same "oldest membership wins" heuristic as
 * `useSettingsFunctionRolesVM` / `useSettingsOrganizationVM`. Members are
 * `forbidden` — this page is gated to Owners + Admins (a harder gate than the
 * function-roles page). Owners + Admins both reach `ready`; write affordances
 * (6.3) are Owner-only via `canManage`.
 *
 * Loads the org's `permission_groups`, the global `capabilities` vocabulary,
 * and the `list_org_members` roster (for per-group member counts) in parallel.
 * Member counts bucket active roster rows by `permission_role_name` (system +
 * custom group names are unique within an org).
 *
 * Create / edit / delete custom groups land in turn 6.3.
 */
export function useSettingsPermissionGroupsVM(): UseSettingsPermissionGroupsVM {
  const { activeOrgId, activeOrgName } = useActiveOrg();
  const [status, setStatus] =
    useState<SettingsPermissionGroupsStatus>('loading');
  const orgId = activeOrgId;
  const [orgName, setOrgName] = useState<string | null>(null);
  const [viewerRole, setViewerRole] = useState<ViewerRoleSystemKey>(null);
  const [groups, setGroups] = useState<PermissionGroup[]>([]);
  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createStatus, setCreateStatus] =
    useState<CreateGroupStatus>('closed');
  const [createError, setCreateError] = useState<CreateGroupError | null>(
    null
  );
  const [draft, setDraft] = useState<GroupDraft | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveGroupStatus>('idle');
  const [saveError, setSaveError] = useState<SaveGroupError | null>(null);
  const [saveToast, setSaveToast] = useState<string | null>(null);
  const [deleteStatus, setDeleteStatus] =
    useState<DeleteGroupStatus>('idle');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteToast, setDeleteToast] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;
      const session = sessionData.session;
      if (!session) {
        setStatus('unauth');
        return;
      }
      // Stay in `loading` until the shared shell resolves the active org.
      if (!activeOrgId) return;

      const { data: membershipRow, error: memErr } = await supabase
        .from('memberships')
        .select('permission_role:permission_role_id(system_key)')
        .eq('user_id', session.user.id)
        .eq('org_id', activeOrgId)
        .maybeSingle();

      if (cancelled) return;
      if (memErr) {
        setStatus('error');
        setError("Couldn't load permission groups. Try again.");
        return;
      }

      type RoleEmbed =
        | { system_key?: string | null }
        | { system_key?: string | null }[]
        | null;
      const row = (membershipRow ?? null) as {
        permission_role: RoleEmbed;
      } | null;
      if (!row) {
        setStatus('empty');
        return;
      }
      const sysKey = Array.isArray(row.permission_role)
        ? row.permission_role[0]?.system_key ?? null
        : row.permission_role?.system_key ?? null;
      const role: ViewerRoleSystemKey =
        sysKey === 'owner' || sysKey === 'admin' || sysKey === 'member'
          ? sysKey
          : null;
      const resolvedOrgId = activeOrgId;

      setOrgName(activeOrgName);
      setViewerRole(role);

      // Members are bounced — this page is Owners + Admins only.
      if (role !== 'owner' && role !== 'admin') {
        setStatus('forbidden');
        return;
      }

      // Load the org's permission_groups + the global capability vocabulary +
      // the member roster (for counts) in parallel.
      const [groupsRes, capsRes, rosterRes] = await Promise.all([
        supabase
          .from('permission_groups')
          .select('id, name, kind, system_key, description, capability_keys')
          .eq('org_id', resolvedOrgId)
          .is('deleted_at', null),
        supabase
          .from('capabilities')
          .select('key, resource, label, description, sort_order')
          .order('sort_order', { ascending: true }),
        supabase.rpc('list_org_members', { p_org_id: resolvedOrgId })
      ]);

      if (cancelled) return;
      if (groupsRes.error || capsRes.error || rosterRes.error) {
        setStatus('error');
        setError("Couldn't load permission groups. Try again.");
        return;
      }

      const groupRows = (groupsRes.data ??
        []) as ReadonlyArray<PermissionGroupRow>;
      const capRows = (capsRes.data ?? []) as ReadonlyArray<CapabilityRow>;
      const rosterRows = (rosterRes.data ??
        []) as ReadonlyArray<MemberRosterRow>;

      // Bucket active members by permission_role_name — system + custom group
      // names are unique within an org.
      const countByName = new Map<string, number>();
      for (const m of rosterRows) {
        if (m.kind !== 'active') continue;
        const groupName = m.permission_role_name;
        if (!groupName) continue;
        countByName.set(groupName, (countByName.get(groupName) ?? 0) + 1);
      }

      const resolvedGroups: PermissionGroup[] = groupRows.map((g) =>
        rowToGroup(g, countByName.get(g.name) ?? 0)
      );

      const resolvedCaps: Capability[] = capRows.map((c) => ({
        key: c.key,
        resource: c.resource,
        label: c.label,
        description: c.description,
        sortOrder: c.sort_order
      }));

      const { system, custom } = sortGroups(resolvedGroups);
      const ordered = [...system, ...custom];

      setGroups(resolvedGroups);
      setCapabilities(resolvedCaps);
      setSelectedId(ordered[0]?.id ?? null);
      setStatus('ready');
    })();
    return () => {
      cancelled = true;
    };
  }, [activeOrgId, activeOrgName]);

  const select = useCallback((id: string) => {
    setSelectedId(id);
    setDraft(null);
    setSaveError(null);
    setSaveStatus('idle');
    setDeleteStatus('idle');
    setDeleteError(null);
  }, []);

  const openCreate = useCallback(() => {
    setCreateError(null);
    setCreateStatus('open');
  }, []);

  const closeCreate = useCallback(() => {
    setCreateError(null);
    setCreateStatus('closed');
  }, []);

  const createGroup = useCallback(
    async (input: CreateGroupInput): Promise<boolean> => {
      if (!orgId) return false;
      const name = input.name.trim();
      const description = input.description.trim();

      // Client validation — mirrors the `create_permission_group` RPC's
      // server-side checks so the user gets instant feedback with no
      // round-trip on a client-catchable failure.
      if (!NAME_PATTERN.test(name)) {
        setCreateError({ field: 'name', message: NAME_FORMAT_ERROR });
        return false;
      }
      if (RESERVED_GROUP_NAMES.includes(name.toLowerCase())) {
        setCreateError({ field: 'name', message: RESERVED_NAME_ERROR });
        return false;
      }
      if (description.length > 280) {
        setCreateError({
          field: 'description',
          message: DESCRIPTION_ERROR
        });
        return false;
      }

      setCreateError(null);
      setCreateStatus('saving');

      const { data, error: rpcErr } = await supabase.rpc(
        'create_permission_group',
        {
          p_org_id: orgId,
          p_name: name,
          p_description: description === '' ? null : description,
          p_clone_from: input.cloneFrom === '' ? null : input.cloneFrom
        }
      );

      if (rpcErr) {
        setCreateError({ field: 'form', message: NETWORK_ERROR });
        setCreateStatus('open');
        return false;
      }

      const envelope = data as
        | { ok: true; group: PermissionGroupRow }
        | { ok: false; error: string }
        | null;

      if (!envelope || envelope.ok !== true) {
        const code = envelope && !envelope.ok ? envelope.error : '';
        if (code === 'invalid_name') {
          setCreateError({ field: 'name', message: NAME_FORMAT_ERROR });
        } else if (code === 'reserved_name') {
          setCreateError({ field: 'name', message: RESERVED_NAME_ERROR });
        } else if (code === 'name_taken') {
          setCreateError({ field: 'name', message: NAME_TAKEN_ERROR });
        } else if (code === 'invalid_description') {
          setCreateError({
            field: 'description',
            message: DESCRIPTION_ERROR
          });
        } else {
          setCreateError({ field: 'form', message: NETWORK_ERROR });
        }
        setCreateStatus('open');
        return false;
      }

      const newGroup = rowToGroup(envelope.group, 0);
      setGroups((prev) => [...prev, newGroup]);
      setSelectedId(newGroup.id);
      setDraft(null);
      setSaveError(null);
      setSaveStatus('idle');
      setCreateError(null);
      setCreateStatus('closed');
      return true;
    },
    [orgId]
  );

  const { system: systemGroups, custom: customGroups } = useMemo(
    () => sortGroups(groups),
    [groups]
  );

  const selectedGroup = useMemo(
    () => groups.find((g) => g.id === selectedId) ?? null,
    [groups, selectedId]
  );

  const canManage = viewerRole === 'owner';

  const toggleCapability = useCallback(
    (key: string) => {
      if (!selectedGroup) return;
      setDraft((prev) => {
        const base = prev ?? seedDraft(selectedGroup);
        const has = base.capabilityKeys.includes(key);
        return {
          ...base,
          capabilityKeys: has
            ? base.capabilityKeys.filter((k) => k !== key)
            : [...base.capabilityKeys, key]
        };
      });
    },
    [selectedGroup]
  );

  const editDraft = useCallback(
    (patch: Partial<Pick<GroupDraft, 'name' | 'description'>>) => {
      if (!selectedGroup) return;
      setDraft((prev) => ({ ...(prev ?? seedDraft(selectedGroup)), ...patch }));
    },
    [selectedGroup]
  );

  const cancelEdit = useCallback(() => {
    setDraft(null);
    setSaveError(null);
    setSaveStatus('idle');
  }, []);

  const clearSaveToast = useCallback(() => {
    setSaveToast(null);
  }, []);

  const isDirty = useMemo(() => {
    if (!draft || !selectedGroup) return false;
    if (draft.name !== selectedGroup.name) return true;
    if (draft.description !== (selectedGroup.description ?? '')) return true;
    const a = [...draft.capabilityKeys].sort();
    const b = [...selectedGroup.capabilityKeys].sort();
    return a.length !== b.length || a.some((k, i) => k !== b[i]);
  }, [draft, selectedGroup]);

  const saveGroup = useCallback(async (): Promise<boolean> => {
    if (!draft || !selectedGroup) return false;
    const name = draft.name.trim();
    const description = draft.description.trim();

    // Client validation — mirrors the rename RPC's server-side checks.
    if (!NAME_PATTERN.test(name)) {
      setSaveError({ field: 'name', message: NAME_FORMAT_ERROR });
      return false;
    }
    if (RESERVED_GROUP_NAMES.includes(name.toLowerCase())) {
      setSaveError({ field: 'name', message: RESERVED_NAME_ERROR });
      return false;
    }
    if (description.length > 280) {
      setSaveError({ field: 'description', message: DESCRIPTION_ERROR });
      return false;
    }

    const capsChanged =
      [...draft.capabilityKeys].sort().join(' ') !==
      [...selectedGroup.capabilityKeys].sort().join(' ');
    const renameChanged =
      name !== selectedGroup.name ||
      description !== (selectedGroup.description ?? '');

    setSaveError(null);
    setSaveStatus('saving');

    let finalRow: PermissionGroupRow | null = null;

    if (capsChanged) {
      const { data, error: rpcErr } = await supabase.rpc(
        'update_permission_group_capabilities',
        {
          p_group_id: selectedGroup.id,
          p_capability_keys: draft.capabilityKeys
        }
      );
      if (rpcErr) {
        setSaveError({ field: 'form', message: NETWORK_ERROR });
        setSaveStatus('idle');
        return false;
      }
      const env = data as GroupRpcEnvelope;
      if (!env || env.ok !== true) {
        const code = env && !env.ok ? env.error : '';
        const message =
          code === 'forbidden'
            ? FORBIDDEN_ERROR
            : code === 'unknown_capability'
              ? UNKNOWN_CAPABILITY_ERROR
              : NETWORK_ERROR;
        setSaveError({ field: 'form', message });
        setSaveStatus('idle');
        return false;
      }
      finalRow = env.group;
    }

    if (renameChanged) {
      const { data, error: rpcErr } = await supabase.rpc(
        'rename_permission_group',
        {
          p_group_id: selectedGroup.id,
          p_name: name,
          p_description: description === '' ? null : description
        }
      );
      if (rpcErr) {
        setSaveError({ field: 'form', message: NETWORK_ERROR });
        setSaveStatus('idle');
        return false;
      }
      const env = data as GroupRpcEnvelope;
      if (!env || env.ok !== true) {
        const code = env && !env.ok ? env.error : '';
        if (code === 'invalid_name') {
          setSaveError({ field: 'name', message: NAME_FORMAT_ERROR });
        } else if (code === 'reserved_name') {
          setSaveError({ field: 'name', message: RESERVED_NAME_ERROR });
        } else if (code === 'name_taken') {
          setSaveError({ field: 'name', message: NAME_TAKEN_ERROR });
        } else if (code === 'invalid_description') {
          setSaveError({ field: 'description', message: DESCRIPTION_ERROR });
        } else if (code === 'forbidden') {
          setSaveError({ field: 'form', message: FORBIDDEN_ERROR });
        } else {
          setSaveError({ field: 'form', message: NETWORK_ERROR });
        }
        setSaveStatus('idle');
        return false;
      }
      finalRow = env.group;
    }

    if (finalRow) {
      const merged = rowToGroup(finalRow, selectedGroup.memberCount);
      setGroups((prev) =>
        prev.map((g) => (g.id === merged.id ? merged : g))
      );
    }
    setDraft(null);
    setSaveError(null);
    setSaveStatus('idle');
    setSaveToast(SAVE_TOAST_MESSAGE);
    return true;
  }, [draft, selectedGroup]);

  const openDelete = useCallback(() => {
    if (!selectedGroup) return;
    setDeleteError(null);
    setDeleteStatus(selectedGroup.memberCount > 0 ? 'blocked' : 'confirm');
  }, [selectedGroup]);

  const closeDelete = useCallback(() => {
    setDeleteStatus('idle');
    setDeleteError(null);
  }, []);

  const deleteGroup = useCallback(
    async (confirmName: string): Promise<boolean> => {
      if (!selectedGroup) return false;

      setDeleteError(null);
      setDeleteStatus('deleting');

      const { data, error: rpcErr } = await supabase.rpc(
        'delete_permission_group',
        { p_group_id: selectedGroup.id, p_confirm_name: confirmName }
      );

      if (rpcErr) {
        setDeleteError(NETWORK_ERROR);
        setDeleteStatus('confirm');
        return false;
      }

      const envelope = data as
        | { ok: true; group_id: string; name: string }
        | { ok: false; error: string; member_count?: number }
        | null;

      if (!envelope || envelope.ok !== true) {
        const code = envelope && !envelope.ok ? envelope.error : '';
        if (code === 'members_assigned') {
          setDeleteError(null);
          setDeleteStatus('blocked');
        } else if (code === 'name_mismatch') {
          setDeleteError(NAME_MISMATCH_ERROR);
          setDeleteStatus('confirm');
        } else if (code === 'forbidden') {
          setDeleteError(FORBIDDEN_ERROR);
          setDeleteStatus('confirm');
        } else {
          setDeleteError(NETWORK_ERROR);
          setDeleteStatus('confirm');
        }
        return false;
      }

      const removedId = envelope.group_id;
      const removedName = envelope.name;
      setGroups((prev) => {
        const next = prev.filter((g) => g.id !== removedId);
        const owner = next.find(
          (g) => g.kind === 'system' && g.systemKey === 'owner'
        );
        setSelectedId(owner?.id ?? next[0]?.id ?? null);
        return next;
      });
      setDraft(null);
      setSaveError(null);
      setSaveStatus('idle');
      setDeleteStatus('idle');
      setDeleteError(null);
      setDeleteToast(removedName);
      return true;
    },
    [selectedGroup]
  );

  const clearDeleteToast = useCallback(() => setDeleteToast(null), []);

  return {
    status,
    orgId,
    orgName,
    viewerRole,
    canManage,
    systemGroups,
    customGroups,
    capabilities,
    selectedId,
    selectedGroup,
    error,
    createStatus,
    createError,
    draft,
    isDirty,
    saveStatus,
    saveError,
    saveToast,
    deleteStatus,
    deleteError,
    deleteToast,
    actions: {
      select,
      openCreate,
      closeCreate,
      createGroup,
      toggleCapability,
      editDraft,
      cancelEdit,
      saveGroup,
      clearSaveToast,
      openDelete,
      closeDelete,
      deleteGroup,
      clearDeleteToast
    }
  };
}
